import { apiEntityFetch } from '..';
import {
  GetMemberPermissionsRes,
  GetPermissionCatalogRes,
  GetRoleMembersRes,
  GetRoleRes,
  GetRolesRes,
  type RoleScopeKind,
  SetRoleScopeBody,
} from './types';

/**
 * The thin call layer over `/entity/v1`'s role-administration API
 * (`gamma-prime-be` branch `feat/GP-3853-permissions-role-engine`,
 * `apps/api/src/contexts/entity/docs/role-admin-api.md`).
 *
 * Kept as a single object behind this module boundary (rather than inlined at
 * each call site) so tests can substitute a fixture via
 * `vi.mock('@/api/entity-roles/client', ...)` — see `./mocks` for that fixture.
 */
export type EntityRolesClient = {
  getCatalog: () => Promise<GetPermissionCatalogRes>;
  getRoles: () => Promise<GetRolesRes>;
  getRole: (_roleId: string) => Promise<GetRoleRes>;
  getRoleMembers: (_roleId: string) => Promise<GetRoleMembersRes>;
  getMemberPermissions: (_userId: string) => Promise<GetMemberPermissionsRes>;
  setRoleScope: (_roleId: string, _kind: RoleScopeKind, _body: SetRoleScopeBody) => Promise<void>;
};

export const entityRolesClient: EntityRolesClient = {
  getCatalog: () => apiEntityFetch<GetPermissionCatalogRes>('/permissions/catalog'),

  getRoles: () => apiEntityFetch<GetRolesRes>('/roles'),

  getRole: (roleId) => apiEntityFetch<GetRoleRes>(`/roles/${encodeURIComponent(roleId)}`),

  getRoleMembers: (roleId) =>
    apiEntityFetch<GetRoleMembersRes>(`/roles/${encodeURIComponent(roleId)}/members`),

  getMemberPermissions: (userId) =>
    apiEntityFetch<GetMemberPermissionsRes>(`/members/${encodeURIComponent(userId)}/permissions`),

  setRoleScope: (roleId, kind, body) =>
    apiEntityFetch<void>(`/roles/${encodeURIComponent(roleId)}/scopes/${kind}`, {
      method: 'PUT',
      body,
    }),
};
